'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

// ─── Types ───────────────────────────────────────────────────────────────────

type SyncDirection = 'push' | 'pull' | 'bidirectional';

interface FieldRow {
  id: string;
  plField: string;
  plLabel: string;
  zohoField: string;
  direction: SyncDirection;
  isCustom?: boolean;
}

interface ZohoFieldMappingProps {
  isLight: boolean;
}

// ─── Default Mappings ─────────────────────────────────────────────────────────

const PROPOSAL_ZOHO_OPTIONS = [
  { value: '', label: '— Not Mapped —' },
  { value: 'Deal_Name', label: 'Deal Name' },
  { value: 'Amount', label: 'Amount' },
  { value: 'Stage', label: 'Stage' },
  { value: 'Closing_Date', label: 'Closing Date' },
  { value: 'Priority', label: 'Priority' },
  { value: 'Owner', label: 'Deal Owner' },
  { value: 'Description', label: 'Description' },
  { value: 'Account_Name', label: 'Account Name' },
  { value: 'Lead_Source', label: 'Lead Source' },
  { value: 'Type', label: 'Type' },
  { value: 'Next_Step', label: 'Next Step' },
  { value: 'Custom_Field_1', label: 'Custom Field 1' },
  { value: 'Custom_Field_2', label: 'Custom Field 2' },
];

const CUSTOMER_ZOHO_OPTIONS = [
  { value: '', label: '— Not Mapped —' },
  { value: 'Account_Name', label: 'Account Name' },
  { value: 'Industry', label: 'Industry' },
  { value: 'Billing_City', label: 'Billing City' },
  { value: 'Account_Type', label: 'Account Type' },
  { value: 'Contact_Name', label: 'Contact Name' },
  { value: 'Email', label: 'Email' },
  { value: 'Phone', label: 'Phone' },
  { value: 'Website', label: 'Website' },
  { value: 'Description', label: 'Description' },
  { value: 'Rating', label: 'Rating' },
  { value: 'Custom_Field_1', label: 'Custom Field 1' },
  { value: 'Custom_Field_2', label: 'Custom Field 2' },
];

const DEFAULT_PROPOSAL_ROWS: FieldRow[] = [
  { id: 'p1', plField: 'proposal_code', plLabel: 'Proposal Code', zohoField: 'Deal_Name', direction: 'push' },
  { id: 'p2', plField: 'expected_value', plLabel: 'Expected Value', zohoField: 'Amount', direction: 'bidirectional' },
  { id: 'p3', plField: 'stage', plLabel: 'Stage', zohoField: 'Stage', direction: 'bidirectional' },
  { id: 'p4', plField: 'due_date', plLabel: 'Due Date', zohoField: 'Closing_Date', direction: 'push' },
  { id: 'p5', plField: 'priority', plLabel: 'Priority', zohoField: 'Priority', direction: 'push' },
  { id: 'p6', plField: 'owner_id', plLabel: 'Owner', zohoField: 'Owner', direction: 'push' },
  { id: 'p7', plField: 'requirement_summary', plLabel: 'Requirement Summary', zohoField: 'Description', direction: 'push' },
  { id: 'p8', plField: 'customer_id', plLabel: 'Customer', zohoField: 'Account_Name', direction: 'push' },
];

const DEFAULT_CUSTOMER_ROWS: FieldRow[] = [
  { id: 'c1', plField: 'name', plLabel: 'Company Name', zohoField: 'Account_Name', direction: 'bidirectional' },
  { id: 'c2', plField: 'industry', plLabel: 'Industry', zohoField: 'Industry', direction: 'push' },
  { id: 'c3', plField: 'location', plLabel: 'Location', zohoField: 'Billing_City', direction: 'push' },
  { id: 'c4', plField: 'status', plLabel: 'Status', zohoField: 'Account_Type', direction: 'push' },
  { id: 'c5', plField: 'contact_name', plLabel: 'Contact Name', zohoField: 'Contact_Name', direction: 'pull' },
  { id: 'c6', plField: 'email', plLabel: 'Email', zohoField: 'Email', direction: 'pull' },
  { id: 'c7', plField: 'phone', plLabel: 'Phone', zohoField: 'Phone', direction: 'pull' },
];

// ─── Direction Config ─────────────────────────────────────────────────────────

const DIRECTION_OPTIONS: { value: SyncDirection; label: string; icon: string; color: string }[] = [
  { value: 'push', label: 'Push →', icon: 'ArrowRightIcon', color: '#1E90FF' },
  { value: 'pull', label: '← Pull', icon: 'ArrowLeftIcon', color: '#F5A623' },
  { value: 'bidirectional', label: '↔ Both', icon: 'ArrowsRightLeftIcon', color: '#00D68F' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function ZohoFieldMapping({ isLight }: ZohoFieldMappingProps) {
  const [activeTab, setActiveTab] = useState<'proposals' | 'customers'>('proposals');
  const [proposalRows, setProposalRows] = useState<FieldRow[]>(DEFAULT_PROPOSAL_ROWS);
  const [customerRows, setCustomerRows] = useState<FieldRow[]>(DEFAULT_CUSTOMER_ROWS);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');
  const [savedBanner, setSavedBanner] = useState(false);

  const cardBg = isLight ? '#FFFFFF' : '#0B1628';
  const borderColor = isLight ? '#E2E8F0' : 'rgba(30,144,255,0.12)';
  const mutedText = isLight ? '#64748B' : '#6B7FA3';
  const headingText = isLight ? '#0F172A' : '#FFFFFF';
  const inputBg = isLight ? '#F8FAFC' : '#0D1829';
  const rowHover = isLight ? '#F1F5F9' : 'rgba(30,144,255,0.03)';

  const rows = activeTab === 'proposals' ? proposalRows : customerRows;
  const setRows = activeTab === 'proposals' ? setProposalRows : setCustomerRows;
  const zohoOptions = activeTab === 'proposals' ? PROPOSAL_ZOHO_OPTIONS : CUSTOMER_ZOHO_OPTIONS;

  const unmappedCount = rows.filter(r => r.zohoField === '').length;

  const updateRow = (id: string, field: Partial<FieldRow>) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...field } : r));
  };

  const removeRow = (id: string) => {
    setRows(prev => prev.filter(r => r.id !== id));
  };

  const addCustomField = () => {
    const newRow: FieldRow = {
      id: `custom_${Date.now()}`,
      plField: '',
      plLabel: 'Custom Field',
      zohoField: '',
      direction: 'push',
      isCustom: true,
    };
    setRows(prev => [...prev, newRow]);
  };

  const handleReset = () => {
    if (activeTab === 'proposals') setProposalRows(DEFAULT_PROPOSAL_ROWS);
    else setCustomerRows(DEFAULT_CUSTOMER_ROWS);
    setSavedBanner(false);
  };

  const handleSave = () => {
    setSavedBanner(true);
    setTimeout(() => setSavedBanner(false), 3000);
  };

  const handleTestMapping = async () => {
    setTestStatus('testing');
    setTestMessage('');
    // Simulate test — in production this would call a real endpoint
    await new Promise(r => setTimeout(r, 1800));
    const hasUnmapped = rows.some(r => r.zohoField === '');
    if (hasUnmapped) {
      setTestStatus('error');
      setTestMessage(`${unmappedCount} field(s) are unmapped. All fields must be mapped before testing.`);
    } else {
      setTestStatus('success');
      setTestMessage(
        activeTab === 'proposals' ?'Test record sent to Zoho CRM as a Deal. All 8 fields landed correctly.' :'Test record sent to Zoho CRM as an Account. All 7 fields landed correctly.'
      );
    }
  };

  const directionConfig = (d: SyncDirection) => DIRECTION_OPTIONS.find(o => o.value === d)!;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
        <div className="flex items-start gap-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-800 text-base"
            style={{ background: 'linear-gradient(135deg, #E42527, #F5A623)', color: '#fff' }}
          >
            Z
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-700" style={{ color: headingText }}>Field Mapping</h3>
            <p className="text-xs mt-1" style={{ color: mutedText }}>
              Define which ProposalLink fields map to Zoho CRM fields, and control sync direction per field.
            </p>
          </div>
          {unmappedCount > 0 && (
            <span
              className="flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full font-700 shrink-0"
              style={{ background: 'rgba(255,71,87,0.12)', color: '#FF4757', border: '1px solid rgba(255,71,87,0.25)' }}
            >
              <Icon name="ExclamationCircleIcon" size={11} />
              {unmappedCount} Unmapped
            </span>
          )}
        </div>

        {/* Entity Tabs */}
        <div className="flex gap-1 mt-5 p-1 rounded-xl" style={{ background: inputBg, border: `1px solid ${borderColor}` }}>
          {([
            { id: 'proposals', label: 'Proposals → Deals', icon: 'DocumentTextIcon' },
            { id: 'customers', label: 'Customers → Accounts', icon: 'BuildingOfficeIcon' },
          ] as const).map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setTestStatus('idle'); setTestMessage(''); }}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-600 transition-all"
              style={{
                background: activeTab === tab.id ? cardBg : 'transparent',
                color: activeTab === tab.id ? '#1E90FF' : mutedText,
                boxShadow: activeTab === tab.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                border: activeTab === tab.id ? `1px solid ${borderColor}` : '1px solid transparent',
              }}
            >
              <Icon name={tab.icon as any} size={13} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Saved Banner */}
      {savedBanner && (
        <div
          className="flex items-center gap-2.5 rounded-xl px-4 py-3"
          style={{ background: 'rgba(0,214,143,0.08)', border: '1px solid rgba(0,214,143,0.25)' }}
        >
          <Icon name="CheckCircleIcon" size={14} style={{ color: '#00D68F', flexShrink: 0 } as React.CSSProperties} />
          <p className="text-xs font-600" style={{ color: '#00D68F' }}>Mapping saved successfully.</p>
        </div>
      )}

      {/* Mapper Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
        {/* Column Headers */}
        <div
          className="grid gap-3 px-4 py-3 text-[10px] font-700 uppercase tracking-wider"
          style={{
            gridTemplateColumns: '1fr 32px 1fr 140px 32px',
            background: inputBg,
            borderBottom: `1px solid ${borderColor}`,
            color: mutedText,
          }}
        >
          <span>ProposalLink Field</span>
          <span />
          <span>Zoho CRM Field</span>
          <span>Sync Direction</span>
          <span />
        </div>

        {/* Rows */}
        <div className="divide-y" style={{ borderColor }}>
          {rows.map((row) => {
            const isUnmapped = row.zohoField === '';
            const dir = directionConfig(row.direction);

            return (
              <div
                key={row.id}
                className="grid gap-3 px-4 py-3 items-center transition-colors"
                style={{
                  gridTemplateColumns: '1fr 32px 1fr 140px 32px',
                  background: isUnmapped ? (isLight ? 'rgba(255,71,87,0.03)' : 'rgba(255,71,87,0.04)') : 'transparent',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = isUnmapped ? (isLight ? 'rgba(255,71,87,0.06)' : 'rgba(255,71,87,0.07)') : rowHover)}
                onMouseLeave={e => (e.currentTarget.style.background = isUnmapped ? (isLight ? 'rgba(255,71,87,0.03)' : 'rgba(255,71,87,0.04)') : 'transparent')}
              >
                {/* ProposalLink Field */}
                <div className="flex items-center gap-2 min-w-0">
                  {isUnmapped && (
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#FF4757' }} />
                  )}
                  {row.isCustom ? (
                    <input
                      value={row.plLabel}
                      onChange={e => updateRow(row.id, { plLabel: e.target.value, plField: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                      className="text-xs font-600 bg-transparent outline-none border-b w-full"
                      style={{ color: headingText, borderColor: 'rgba(30,144,255,0.3)' }}
                      placeholder="Field label"
                    />
                  ) : (
                    <div className="min-w-0">
                      <p className="text-xs font-600 truncate" style={{ color: headingText }}>{row.plLabel}</p>
                      <p className="text-[10px] font-500 truncate" style={{ color: mutedText }}>{row.plField}</p>
                    </div>
                  )}
                </div>

                {/* Arrow */}
                <div className="flex items-center justify-center">
                  <Icon
                    name={
                      row.direction === 'push' ? 'ArrowRightIcon' :
                      row.direction === 'pull'? 'ArrowLeftIcon' : 'ArrowsRightLeftIcon'
                    }
                    size={14}
                    style={{ color: dir.color } as React.CSSProperties}
                  />
                </div>

                {/* Zoho CRM Field Dropdown */}
                <div className="relative">
                  <select
                    value={row.zohoField}
                    onChange={e => updateRow(row.id, { zohoField: e.target.value })}
                    className="w-full text-xs font-500 rounded-lg px-3 py-2 appearance-none cursor-pointer outline-none transition-all"
                    style={{
                      background: isUnmapped
                        ? (isLight ? 'rgba(255,71,87,0.06)' : 'rgba(255,71,87,0.08)')
                        : inputBg,
                      border: `1px solid ${isUnmapped ? 'rgba(255,71,87,0.35)' : borderColor}`,
                      color: isUnmapped ? '#FF4757' : headingText,
                      paddingRight: 28,
                    }}
                  >
                    {zohoOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <Icon
                    name="ChevronDownIcon"
                    size={11}
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: mutedText, pointerEvents: 'none' } as React.CSSProperties}
                  />
                </div>

                {/* Sync Direction Toggle */}
                <div className="flex items-center gap-0.5 rounded-lg p-0.5" style={{ background: inputBg, border: `1px solid ${borderColor}` }}>
                  {DIRECTION_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => updateRow(row.id, { direction: opt.value })}
                      title={opt.label}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-[10px] font-700 transition-all"
                      style={{
                        background: row.direction === opt.value ? `${opt.color}18` : 'transparent',
                        color: row.direction === opt.value ? opt.color : mutedText,
                        border: row.direction === opt.value ? `1px solid ${opt.color}35` : '1px solid transparent',
                      }}
                    >
                      <Icon name={opt.icon as any} size={10} />
                      <span className="hidden sm:inline">{opt.value === 'push' ? 'Push' : opt.value === 'pull' ? 'Pull' : 'Both'}</span>
                    </button>
                  ))}
                </div>

                {/* Remove (custom only) */}
                <div className="flex items-center justify-center">
                  {row.isCustom ? (
                    <button
                      onClick={() => removeRow(row.id)}
                      className="p-1.5 rounded-lg transition-all"
                      style={{ color: '#FF4757' }}
                      title="Remove field"
                    >
                      <Icon name="TrashIcon" size={12} />
                    </button>
                  ) : (
                    <div className="w-6" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Add Custom Field */}
        <div className="px-4 py-3" style={{ borderTop: `1px solid ${borderColor}`, background: inputBg }}>
          <button
            onClick={addCustomField}
            className="flex items-center gap-2 text-xs font-600 px-3 py-2 rounded-lg transition-all"
            style={{ background: 'rgba(30,144,255,0.08)', color: '#1E90FF', border: '1px solid rgba(30,144,255,0.2)' }}
          >
            <Icon name="PlusIcon" size={13} />
            Add Custom Field
          </button>
        </div>
      </div>

      {/* Unmapped Warning */}
      {unmappedCount > 0 && (
        <div
          className="flex items-start gap-3 rounded-xl px-4 py-3"
          style={{ background: 'rgba(255,71,87,0.06)', border: '1px solid rgba(255,71,87,0.2)' }}
        >
          <Icon name="ExclamationTriangleIcon" size={14} style={{ color: '#FF4757', marginTop: 1, flexShrink: 0 } as React.CSSProperties} />
          <div>
            <p className="text-xs font-700" style={{ color: '#FF4757' }}>
              {unmappedCount} field{unmappedCount > 1 ? 's are' : ' is'} not mapped
            </p>
            <p className="text-[10px] mt-0.5" style={{ color: mutedText }}>
              Unmapped fields will be skipped during sync. Assign a Zoho CRM field or remove the row.
            </p>
          </div>
        </div>
      )}

      {/* Test Result */}
      {testStatus !== 'idle' && (
        <div
          className="flex items-start gap-3 rounded-xl px-4 py-3"
          style={{
            background: testStatus === 'success' ? 'rgba(0,214,143,0.06)' : testStatus === 'error' ? 'rgba(255,71,87,0.06)' : 'rgba(30,144,255,0.06)',
            border: `1px solid ${testStatus === 'success' ? 'rgba(0,214,143,0.25)' : testStatus === 'error' ? 'rgba(255,71,87,0.25)' : 'rgba(30,144,255,0.25)'}`,
          }}
        >
          <Icon
            name={testStatus === 'testing' ? 'ArrowPathIcon' : testStatus === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'}
            size={14}
            className={testStatus === 'testing' ? 'animate-spin' : ''}
            style={{
              color: testStatus === 'success' ? '#00D68F' : testStatus === 'error' ? '#FF4757' : '#1E90FF',
              marginTop: 1,
              flexShrink: 0,
            } as React.CSSProperties}
          />
          <div className="flex-1">
            <p
              className="text-xs font-700"
              style={{ color: testStatus === 'success' ? '#00D68F' : testStatus === 'error' ? '#FF4757' : '#1E90FF' }}
            >
              {testStatus === 'testing' ? 'Sending test record to Zoho CRM…' : testStatus === 'success' ? 'Test Passed' : 'Test Failed'}
            </p>
            {testMessage && (
              <p className="text-[10px] mt-0.5" style={{ color: mutedText }}>{testMessage}</p>
            )}
          </div>
          {testStatus !== 'testing' && (
            <button onClick={() => { setTestStatus('idle'); setTestMessage(''); }} style={{ color: mutedText }}>
              <Icon name="XMarkIcon" size={13} />
            </button>
          )}
        </div>
      )}

      {/* Action Bar */}
      <div className="flex items-center justify-between gap-3 rounded-xl px-5 py-4" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
        <button
          onClick={handleReset}
          className="flex items-center gap-2 text-xs font-600 px-4 py-2 rounded-lg transition-all"
          style={{ background: inputBg, color: mutedText, border: `1px solid ${borderColor}` }}
        >
          <Icon name="ArrowPathIcon" size={13} />
          Reset to Default
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={handleTestMapping}
            disabled={testStatus === 'testing'}
            className="flex items-center gap-2 text-xs font-700 px-4 py-2 rounded-lg transition-all disabled:opacity-60"
            style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF', border: '1px solid rgba(30,144,255,0.25)' }}
          >
            <Icon
              name={testStatus === 'testing' ? 'ArrowPathIcon' : 'BeakerIcon'}
              size={13}
              className={testStatus === 'testing' ? 'animate-spin' : ''}
            />
            {testStatus === 'testing' ? 'Testing…' : 'Test Mapping'}
          </button>

          <button
            onClick={handleSave}
            className="flex items-center gap-2 text-xs font-700 px-5 py-2 rounded-lg transition-all"
            style={{
              background: 'linear-gradient(135deg, #E42527, #F5A623)',
              color: '#fff',
              boxShadow: '0 2px 8px rgba(228,37,39,0.25)',
            }}
          >
            <Icon name="CheckIcon" size={13} />
            Save Mapping
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-5 px-1">
        {DIRECTION_OPTIONS.map(opt => (
          <div key={opt.value} className="flex items-center gap-1.5">
            <Icon name={opt.icon as any} size={11} style={{ color: opt.color } as React.CSSProperties} />
            <span className="text-[10px] font-600" style={{ color: mutedText }}>{opt.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: '#FF4757' }} />
          <span className="text-[10px] font-600" style={{ color: mutedText }}>Unmapped field</span>
        </div>
      </div>
    </div>
  );
}
